import React, { useState, useEffect, useContext, useMemo } from 'react';
import once from 'lodash.once';
import { JSONTree } from 'react-json-tree';
import { Form, FormContext } from '@trp-beacon-ui-kit/react-controls';
import debounce from 'lodash.debounce';
import deepFreeze from 'deep-freeze';
import { useHistory } from 'react-router-dom';
import { isDevelopment } from '../config';
import {
  ApplicationState,
  StateStore,
  StateStoreOrVoid,
  getInitialState
} from './types/application-state';
import {
  merge,
  getCurrentRoute,
  parseCurrentQueryString,
  isEmptyObject,
  trimInputObj,
  getLastVisitedRoute,
  getBackRoute
} from './util';
import { setFormErrorsForScreenReader } from './htmlUtils';
import { FlowLike, FlowNode, PropsMap } from './types/flow';
import { InputField } from './types/input-field';
import { DataLoader } from './types/data-loader';
import { persistState } from './state-persistence';
import ErrorBoundary, {
  ErrorBoundaryContext
} from '../components/ErrorBoundary/ErrorBoundary';
import {
  RegistrationContainer,
  DataContainer,
  flowsToRegistrationContainer
} from './flow-register';

import { NoValidActionTakenError, NodeNotFoundError } from './errors';
import { matchAction } from './match-action';
import {
  NavAction,
  ModalAction,
  RouteAction,
  GoBackAction,
  RJPutOnPathAction,
  FinishAction,
  PartialUpdateAction,
  GoBackRouteAction
} from './actions';
import Loader from '../components/Loader/Loader';
import { InputMap } from './types/input-map';
import { fundingTypes } from '../flows/shared/utils';

export const inputsToNodeMap: WeakMap<FlowNode, InputMap> = new WeakMap();

export const FlowContext = React.createContext({});

export const DEFAULT_LOADING_MESSAGE = 'Loading Application';
const DEFAULT_COMMIT_MESSAGE = 'Saving Application';

export const RETURN_JOURNEY_NAMESPACE = 'return-journey';
export const isReturnJourney = ns => ns.startsWith(RETURN_JOURNEY_NAMESPACE);

export const FRONT_OFFICE_NAMESPACE = 'front-office';
export const isFONASUJourney = ns => ns.startsWith(FRONT_OFFICE_NAMESPACE);

// account for edit mode on review  page when triggering review node commit function
// stops current createApplication message, "Creating Your Account", from showing when editing
export const REVIEW_COMMIT_MESSAGE = { message: DEFAULT_COMMIT_MESSAGE };

const mergeState = (
  appState: ApplicationState,
  updates: StateStoreOrVoid
): ApplicationState => {
  const clonedStore: StateStore = { ...appState.store };
  // @TODO keep track of prior state
  // may be useful for FO NASU and reviewing state changes
  // pass in a cloned version of the application state
  const result = {
    ...appState,
    store: deepFreeze(merge(clonedStore, updates))
  };
  return result;
};

export const popOneFromProgress: DataLoader = ({ store }) => {
  const { progressStepText: lastProgressStepText } = store;
  const currentRoute = getCurrentRoute();
  const onCurrentRoute =
    lastProgressStepText &&
    currentRoute === getLastVisitedRoute(lastProgressStepText);

  const progressStepText = (lastProgressStepText || '')
    .split(',')
    .slice(0, onCurrentRoute ? -2 : -1)
    .filter(Boolean)
    .join(',');

  return { app: {}, progressStepText };
};

const newState = async (
  appState: ApplicationState,
  setAppState: Function,
  setAlert: Function,
  triggerError: Function,
  loader?: DataLoader,
  flowProps?: PropsMap
) => {
  try {
    if (loader) {
      const nextState = mergeState(appState, await loader(appState, flowProps));
      persistState(nextState);
      setAppState(nextState);
      return nextState;
    }
    return appState;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('nextState catching error+++', err);
    setAlert(true);
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    triggerError({ error: err }, isFONASUJourney(getCurrentRoute()));
    return err;
  }
};

/** NavActionHandlers Start* */
const RouteActionHandler = ({
  reg,
  node,
  history,
  action,
  state
}: {
  reg: RegistrationContainer;
  node: FlowNode;
  history;
  action: RouteAction;
  state: ApplicationState;
}) => {
  const nextSegment = `/${reg.nodeToNamespace.get(node)}/${action.nextPath}`;
  const {
    store: { routeData }
  } = state;

  return history.push(
    routeData?.queryString
      ? `${nextSegment}${routeData.queryString}`
      : nextSegment
  );
};

const RJPutOnPathActionHandler = ({
  state,
  history
}: {
  state: ApplicationState;
  history;
}) => {
  const {
    store: { routeData }
  } = state;

  // When we send the user to CLX, there is no rjRoute...
  if (!routeData?.rjRoute) {
    return undefined;
  }

  const continuePath = `/${routeData?.rjRoute}`;
  return history.push(continuePath);
};

export const GoBackActionHandler = ({ state, history }) => {
  const { progressStepText } = state.store;
  const backRoute = getBackRoute(progressStepText);

  history.push(`/${backRoute}`);
};

const GoBackRouteActionHandler = ({
  reg,
  node,
  history,
  state,
  chosen
}: {
  reg: RegistrationContainer;
  node: FlowNode;
  history;
  state: ApplicationState;
  chosen: string;
}) => {
  const nextSegment = `/${reg.nodeToNamespace.get(node)}/${chosen}`;
  const {
    store: { routeData }
  } = state;

  return history.push(
    routeData?.queryString
      ? `${nextSegment}${routeData.queryString}`
      : nextSegment
  );
};

const FinishActionHandler = () => 'You have finished your journey!!';

const NavActionHandlers = new Map<NavAction, Function>([
  [RouteAction, RouteActionHandler],
  [RJPutOnPathAction, RJPutOnPathActionHandler],
  [GoBackAction, GoBackActionHandler],
  [GoBackRouteAction, GoBackRouteActionHandler],
  [FinishAction, FinishActionHandler]
]);

type NavActionHandlerArgs = {
  reg: RegistrationContainer;
  node: FlowNode;
  history;
  action: NavAction;
  state: ApplicationState;
  chosen?: string;
};

const handleNavAction = (args: NavActionHandlerArgs): any => {
  const handler = NavActionHandlers.get(args.action.constructor);
  if (!handler) {
    throw new NoValidActionTakenError(
      `An invalid action was taken when navigating: (action: ${JSON.stringify(
        args.action,
        null,
        4
      )})(node: ${JSON.stringify(args.node, null, 4)})`
    );
  }
  // For Back Navigation
  if (args.action instanceof GoBackAction) {
    window.scroll({
      top: 0
    });
  }
  return handler(args);
};

const checkbox = input => input.type === 'checkbox';

const loadSavedDataIntoFormProps = (
  form?: InputField[],
  lastSavedData?: { [x: string]: any }
): { [x: string]: InputField } =>
  (form || [])
    .map(({ name, ...input }) => ({
      name,
      ...input,
      // pull last form field value from savedInputs in store
      [checkbox(input) ? 'defaultChecked' : 'defaultValue']: lastSavedData?.[
        name
      ]
    }))
    .reduce((acc, input) => ({ ...acc, [input.name]: input }), {});

export const combineDecorators = pageDecorators => ({ children }) => {
  if (!pageDecorators) return children;

  if (!Array.isArray(pageDecorators) && typeof pageDecorators !== 'function') {
    throw new TypeError(
      'pageDecorators should either be  array of components or a single component'
    );
  }

  return (Array.isArray(pageDecorators)
    ? pageDecorators
    : [pageDecorators]
  ).reduce(
    (decorated, Decorator) => <Decorator>{decorated}</Decorator>,
    children
  );
};

const Debug = ({ appState }) => (
  <div style={{ marginTop: 16, marginBottom: 16 }}>
    <JSONTree data={appState} />
  </div>
);

const mergeFlowProps = (flowProps, nodeProps) =>
  merge(flowProps || {}, nodeProps || {});

const getLastSavedData = (appState, ns) => appState?.savedInputs?.[ns];

// transforms the FlowNodes form section so validation
// has appState applied
const bindApplicationStateToValidators = (appState, formProps) => {
  // this function
  // takes an input from the nodes form section
  // and rebinds the validation function with appState
  const bindValidation = input => {
    const res = {
      ...input
    };

    if (input.validation) {
      res.validation = (typeof input.validation === 'function'
        ? [input.validation]
        : input.validation
      ).map(fn => (val, context) => fn(val, context, appState));
    }
    return res;
  };

  // here we iterate through all of the  form
  // inputs and bind appState to their validators
  return Object.keys(formProps).reduce(
    (acc, key) => ({
      ...acc,
      [key]: bindValidation(formProps[key])
    }),
    {}
  );
};

const makeDebug = appState => isDevelopment() && <Debug appState={appState} />;

const clickContinue = debounce(() => {
  // @TODO the issue is we pass things through the chosen value of
  // the next function sooo... we can't just submit the form without a value
  // there needs to be away to set next state via the form component
  const continueBtn: any = document.querySelector(
    'nasu-mf-beacon-button.modal-continue-btn, nasu-mf-beacon-button.continue-btn'
  );
  return continueBtn?.click?.();
});

const runOnce = once((fn, ...args) => fn(...args));

const submitHandler = ev => {
  ev.preventDefault();
  return clickContinue();
};

const enterHandler = ev => {
  if (
    ev.key === 'Enter' &&
    (ev.target.type === 'text' ||
      ev.target.type === 'email' ||
      ev.target.type === 'password')
  ) {
    clickContinue();
  }
};

const addSubmitHandler = () => {
  window.addEventListener('keypress', enterHandler);

  if (document.forms[0])
    document.forms[0].addEventListener('submit', submitHandler);
};

const removeSubmitHandler = () => {
  window.removeEventListener('keypress', enterHandler);

  if (document.forms[0])
    document.forms[0].removeEventListener('submit', submitHandler);
};

const DecoratedContents = ({
  pageDecorators,
  appState,
  next,
  skipValidation,
  back,
  debug,
  flowProps,
  formProps,
  node,
  ns,
  closeNextModal,
  commitMessage,
  NextModal,
  CurrentScreen,
  alert
}) => {
  // we wrap the screen in any pageDecorators.
  // this is useful for stuff like page layouts shared by all screens
  // or scripts/other things added around the screen on each node.
  // inspired by https://github.com/storybookjs/storybook/blob/next/lib/client-api/src/decorators.ts#L18
  const DecoratorsWrapper = useMemo(
    () => combineDecorators(pageDecorators),
    []
  );

  /* eslint-disable react/jsx-no-constructed-context-values */
  const screenProps = {
    appState,
    back,
    skipValidation,
    next,
    debug,
    flowProps: mergeFlowProps(flowProps, node.flowProps),
    formProps,
    closeNextModal,
    queryString: parseCurrentQueryString(),
    alert
  };

  return (
    <FlowContext.Provider value={screenProps}>
      <DecoratorsWrapper>
        {/*
            @TODO [2020-05-25]: similarly to the "loadingMessage" perhaps this should be "isCommitting"
            If node is still committing/saving data we should this loading screen + commit message
            This is different because the Screen contents are still rendered when committing
            , but not when loading. This is useful in certain cases (e.g. openModal)
         */}
        {Boolean(commitMessage) && (
          <Loader
            message={commitMessage?.message}
            secondaryMessage={commitMessage?.secondaryMessage}
            showFONASUSpinner={isFONASUJourney(ns)}
          />
        )}
        {Boolean(NextModal) && <NextModal {...screenProps} />}
        <CurrentScreen {...screenProps} />
      </DecoratorsWrapper>
    </FlowContext.Provider>
  );
};

export const FormContents = ({
  back,
  skipValidation,
  node,
  loadingMessage,
  commitMessage,
  alert,
  appState,
  ns,
  NextModal,
  closeNextModal,
  flowData
}) => {
  const { submit }: any = useContext(FormContext);

  // While the node is still loading data we show the loading screen + message
  //
  //
  // TODO @charlie [2020-05-25]: We should really call this something other than
  // "loadingMessage" perhaps "isLoading" would be more descriptive of a variable name
  // and have the loadingMessage a seperate variable.
  if (loadingMessage) return <Loader message={loadingMessage} />;

  // Load the error screen for any API errors on load/commit

  const next = (chosen, result) => async () => {
    // modals should be closed when continuing
    if (chosen !== 'skipCloseNextModal') closeNextModal();
    const res = submit(chosen, result);
    const [errors] = res;
    if (isEmptyObject(errors)) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
    return res;
  };

  // reference to the current nodes screen
  const CurrentScreen = node.screen;

  // any saved form data from the screen
  const lastSavedData = getLastSavedData(appState, ns);

  // here we partially apply the appState to our form validators
  const formProps = bindApplicationStateToValidators(
    appState,
    // we decorate the formProps with that saved form data
    // TODO [2020-06-05]: localStorage gone this needs to be removed
    loadSavedDataIntoFormProps(node.form, lastSavedData)
  );

  // we provide a tool for debugging to the flowContext
  // TODO @charlie [2020-05-25] this should be removed in the production build
  const debug = makeDebug(appState);

  // gather all the props to pass to the flowContext
  const { flowProps, pageDecorators } = flowData;

  return (
    <DecoratedContents
      pageDecorators={pageDecorators}
      appState={appState}
      next={next}
      skipValidation={skipValidation}
      back={back}
      debug={debug}
      flowProps={flowProps}
      formProps={formProps}
      node={node}
      ns={ns}
      closeNextModal={closeNextModal}
      commitMessage={commitMessage}
      NextModal={NextModal}
      CurrentScreen={CurrentScreen}
      alert={alert}
    />
  );
};

// after the commit we invoke the onCommitHook
// this allows any side effects from the commit
// e.g. return journey + profile-service
const onEventHook = async (
  onEvent: DataLoader | undefined,
  stateAfterEvent: ApplicationState,
  triggerError: Function,
  flowProps?: PropsMap
): Promise<StateStoreOrVoid> => {
  // if an error happens here we will just swallow it and log
  try {
    if (onEvent) {
      const res = await onEvent(stateAfterEvent, flowProps);
      return res;
    }
  } catch (err) {
    triggerError({ error: err }, isFONASUJourney(getCurrentRoute()));
  }
  return {};
};

const parseMultiLoaderStates = async (
  node,
  eventHooks,
  onEventState,
  triggerError
): Promise<StateStoreOrVoid> => {
  // map through each load func getting updated app state from each func
  const eventRes = await eventHooks.map(hook =>
    onEventHook(hook, onEventState, triggerError, node.flowProps)
  );
  // reduce app state response array from above, into a state store object
  const stateStoreRes = eventRes.reduce((acc, cur) => cur, {});
  return stateStoreRes;
};

const makeEventFn = (
  node: FlowNode,
  triggerError: Function,
  event?: DataLoader,
  eventHook?: DataLoader | DataLoader[]
): DataLoader => async (state: ApplicationState): Promise<StateStoreOrVoid> => {
  const commitResults = event ? await event(state, node.flowProps) : {};
  const onEventState: ApplicationState = mergeState(state, commitResults);
  let hookResults: StateStoreOrVoid;

  // account for mutliple loader functions
  if (Array.isArray(eventHook)) {
    hookResults = await parseMultiLoaderStates(
      node,
      eventHook,
      onEventState,
      triggerError
    );
  } else {
    hookResults = await onEventHook(
      eventHook,
      onEventState,
      triggerError,
      node.flowProps
    );
  }

  return { ...commitResults, ...hookResults };
};

const onLoadGoToStart = (history, namespace) => {
  if (!isReturnJourney(namespace) && !isFONASUJourney(namespace)) {
    if (!isDevelopment) {
      // persist any url queries present (i.e. funds carrying over from MFRT)
      history.replace(`/${namespace}${history?.location?.search}`);
    }
  }
};

export const WithoutErrorHandling = ({
  reg
}: {
  reg: RegistrationContainer;
}) => {
  const triggerError: Function = useContext(ErrorBoundaryContext);

  const history = useHistory();

  const [NextModal, setNextModal] = useState(false);

  const [appState, setAppState]: [ApplicationState, Function] = useState(
    getInitialState()
  );

  const [loadingMessage, setLoadingMessage]: [
    boolean | string,
    Function
  ] = useState(DEFAULT_LOADING_MESSAGE);
  const [commitMessage, setCommitMessage]: [
    boolean | Record<string, any>,
    Function
  ] = useState(false);
  const [alert, setAlert]: [boolean, Function] = useState(false);

  // if the modal is open then close it
  const closeNextModal = () => NextModal && setNextModal(false);

  const ns = getCurrentRoute();
  const node = reg.flowRoutes.get(ns);

  const flowNamespace = node && reg.nodeToNamespace.get(node);

  const flowData: DataContainer | undefined = reg.namespaceToDataContainer.get(
    flowNamespace || ''
  );

  // when not in development mode we need to go to the start of the flow
  // if it is an initial load.
  runOnce(() => {
    onLoadGoToStart(history, flowNamespace);
    if (flowData?.onApplicationLoad) {
      flowData.onApplicationLoad();
    }
  });

  if (!node) {
    throw new NodeNotFoundError(
      `A node was not found matching that namespace "${ns}" `
    );
  }

  const handleSpecialAction = specialAction => {
    if (specialAction instanceof ModalAction) {
      setNextModal(() => specialAction.contents);
    }

    setCommitMessage(false);
  };

  useEffect(() => {
    (async () => {
      setLoadingMessage(node.load?.message || DEFAULT_LOADING_MESSAGE);
      const loadFn = makeEventFn(
        node,
        triggerError,
        node.load,
        flowData?.onLoad
      );

      const stateAfterLoad = await newState(
        appState,
        setAppState,
        setAlert,
        triggerError,
        loadFn,
        node.flowProps
      );

      setLoadingMessage(false);
      setAlert(false);

      const matchedAction = matchAction(
        { ...appState },
        stateAfterLoad
      )(...(node.postLoad?.cases || []))(node.next.default);

      const specialActions = [ModalAction, PartialUpdateAction];
      if (
        specialActions.some(
          specialAction => matchedAction instanceof specialAction
        )
      ) {
        handleSpecialAction(matchedAction);
        return;
      }

      closeNextModal();
      addSubmitHandler();
    })();
    return removeSubmitHandler;
  }, [node]);

  const back = (stateAfterCommit?) => async () => {
    closeNextModal();
    if (typeof stateAfterCommit === 'string') {
      return handleNavAction({
        reg,
        node,
        history,
        action: new GoBackRouteAction(),
        state: appState,
        chosen: stateAfterCommit
      });
    }
    const state = { ...appState, ...stateAfterCommit };

    await newState(
      state,
      setAppState,
      setAlert,
      triggerError,
      popOneFromProgress,
      node.flowProps
    );

    return handleNavAction({
      reg,
      node,
      history,
      action: new GoBackAction(),
      state
    });
  };

  /* eslint-disable */
  const onSubmit = async (errors, untrimmedInputs, chosen, result) => {
    if (errors) {
      return setFormErrorsForScreenReader(errors);
    }

    // ignore cases where validation is purposefully being skipped
    if (
      /^dangerouslySkipValidation:/.test(chosen) ||
      /runValidation/.test(chosen)
    )
      return;

    const inputs = untrimmedInputs
      ? trimInputObj(untrimmedInputs)
      : untrimmedInputs;
    const savedInputs = { ...appState.savedInputs, [ns]: inputs };
    const currentState = { ...appState, inputs, savedInputs, chosen, result };

    const commitWithPossibleLoadingMessage: DataLoader = (
      state: ApplicationState,
      flowProps?: PropsMap
    ) => {
      const resultOrPromise = node.commit?.(state, flowProps);
      if (resultOrPromise instanceof Promise) {
        setCommitMessage(() => {
          // show "Saving Application" message instead of createApplication's, "Creating Your Account"
          const displayMessage = {
            message: result?.message || node.commit?.message,
            secondaryMessage: result?.message
              ? result.message.secondaryMessage
              : node.commit?.secondaryMessage
          };
          return {
            message: displayMessage.message || DEFAULT_COMMIT_MESSAGE,
            secondaryMessage: displayMessage.secondaryMessage
          };
        });
      }
      return resultOrPromise;
    };
    const commitFn = makeEventFn(
      node,
      triggerError,
      commitWithPossibleLoadingMessage,
      flowData?.onCommit
    );

    const stateAfterCommit = await newState(
      currentState,
      setAppState,
      setAlert,
      triggerError,
      commitFn,
      node.flowProps
    );

    // if updating and going backward instead of forward
    const navigatingBackward = result === 'updateAndBack';
    if (navigatingBackward) return back(stateAfterCommit)();

    const matchedAction = matchAction(
      { ...currentState },
      stateAfterCommit
    )(...(node.next.cases || []))(node.next.default);

    const specialActions = [ModalAction, PartialUpdateAction];
    if (
      specialActions.some(
        specialAction => matchedAction instanceof specialAction
      )
    ) {
      handleSpecialAction(matchedAction);
      return;
    }

    setCommitMessage(false);
    if (stateAfterCommit) {
      setLoadingMessage(DEFAULT_LOADING_MESSAGE);
      setAlert(false);
      handleNavAction({
        reg,
        node,
        history,
        action: matchedAction,
        state: stateAfterCommit
      });
    }

    // stop loading spinner for FONASU/OCR flows
    if (isFONASUJourney(flowNamespace)) {
      setLoadingMessage(false);
    }
    // stop loading screen toggling multiple times on goBack
    else if (!(matchedAction instanceof GoBackAction)) {
      setLoadingMessage(false);
    }
  };
  /* eslint-enable */

  const skipValidation = results => () =>
    onSubmit(null, null, fundingTypes.CHECK, results);

  return (
    <Form onSubmit={onSubmit}>
      <form>
        <FormContents
          skipValidation={skipValidation}
          back={back}
          node={node}
          ns={ns}
          loadingMessage={loadingMessage}
          commitMessage={commitMessage}
          alert={alert}
          appState={appState}
          NextModal={NextModal}
          flowData={flowData}
          closeNextModal={closeNextModal}
        />
      </form>
    </Form>
  );
};

const FlowEngine = ({ flows }: { flows: FlowLike[] }) => {
  return (
    <ErrorBoundary>
      <WithoutErrorHandling reg={flowsToRegistrationContainer(flows)} />
    </ErrorBoundary>
  );
};

export default FlowEngine;
